<template>
  <!-- Kept outside the overlay's v-if: a live region inserted already holding
       text is skipped by some screen readers, so this one exists from the
       start and only its text changes. -->
  <span class="visually-hidden" role="status" aria-live="polite">{{ announcement }}</span>

  <Transition name="boot-veil">
    <!-- The veil blurs the skeleton page behind it; clicking it (or Esc)
         waves the overlay away and leaves the skeletons, as before. -->
    <div v-if="visible" class="boot-veil" aria-hidden="true" @click.self="dismiss">
      <div class="boot-card" :class="{ 'is-done': settling }">
        <!-- Transactions streaming past: placeholder rows until the expenses
             response lands, then the user's own most recent ones. -->
        <div class="boot-stream">
          <!-- type="transition": the track also runs an endless scroll
               animation, and without this Vue waits on that (which never
               ends) instead of the fade, leaving the stream blank. -->
          <Transition name="boot-swap" mode="out-in" type="transition">
            <div :key="streamKey" class="boot-track" :style="{ '--rows': streamItems.length }">
              <div v-for="(r, i) in streamLoop" :key="i" class="boot-tx">
                <span class="boot-tx-dot" :style="{ background: r.color }" />
                <span v-if="r.name" class="boot-tx-name">{{ r.name }}</span>
                <span v-else class="boot-tx-bar" :style="{ width: `${r.w}%` }" />
                <span v-if="r.amount != null" class="boot-tx-amt num">{{ fmt$c(r.amount) }}</span>
                <span v-else class="boot-tx-bar boot-tx-bar-amt" />
              </div>
            </div>
          </Transition>
          <!-- Done: the stream dims and a check draws over it. -->
          <svg v-if="settling" class="boot-check" viewBox="0 0 52 52">
            <circle class="boot-check-ring" cx="26" cy="26" r="23" />
            <path class="boot-check-tick" d="M15.5 27.5l7 7 14-15" />
          </svg>
        </div>

        <h2 class="boot-title">{{ settling ? "You're all set" : 'Getting everything ready' }}</h2>

        <TransitionGroup tag="ol" name="boot-line" class="boot-lines">
          <li v-for="line in lines" :key="line.id" class="boot-line" :class="`is-${line.state}`">
            <span class="boot-mark">
              <span v-if="line.state === 'active'" class="boot-spinner" />
              <Icon v-else :name="line.state === 'failed' ? 'x' : 'check'" :size="13" :stroke-width="2.25" />
            </span>
            <span class="boot-text">{{ line.text }}</span>
          </li>
        </TransitionGroup>

        <!-- One segment per step: filled when done, shimmering while running.
             Step-based, never a guessed percentage. -->
        <div class="boot-progress">
          <span v-for="line in lines" :key="line.id" class="boot-seg" :class="`is-${line.state}`" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
// What a cold load is doing, told as a checklist of the real steps over a
// blurred page. The steps and the show/hide timing live in useBootStatus;
// this only draws them.
import { computed, onBeforeUnmount, onMounted } from 'vue'
import Icon from './Icon.vue'
import { fmt$c } from '@/utils/format'
import { useAuth } from '@/composables/useAuth'
import { useBootNarrator, useShellBootSteps } from '@/composables/useBootStatus'

useShellBootSteps(useAuth() as any)
const { lines, visible, settling, announcement, streamRows, dismiss } = useBootNarrator()

interface StreamItem {
  name?: string
  amount?: number
  color: string
  /** Placeholder bar width, %. */
  w?: number
}

// Placeholder rows: fixed widths so the stream looks like a ledger rather
// than a pulsing block, tinted with the category palette.
const PLACEHOLDER: StreamItem[] = [62, 44, 71, 38, 55, 67, 47, 59].map((w, i) => ({
  w,
  color: `var(--cat-${i + 1})`,
}))

const streamItems = computed<StreamItem[]>(() =>
  streamRows.value.length >= 4 ? streamRows.value : PLACEHOLDER,
)
const streamKey = computed(() => (streamItems.value === PLACEHOLDER ? 'placeholder' : 'real'))
// Rendered twice so the upward scroll can loop without a seam.
const streamLoop = computed(() => [...streamItems.value, ...streamItems.value])

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && visible.value) dismiss()
}
onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
</script>

<style scoped>
/* Covers the whole window, nav included, so the card is the one thing in
   focus while the numbers load behind the glass. */
.boot-veil {
  position: fixed;
  /* Over the page, the nav and the mobile top bar; under the drawer scrim (40)
     and every dialog — the unlock prompt in particular sits on top of it. */
  z-index: 30;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
  background: color-mix(in oklab, var(--bg) 50%, transparent);
  /* Prefixed FIRST. The production CSS minifier folds the pair into one
     property and keeps whichever comes last; with -webkit- last it shipped
     only the prefixed form, which Firefox ignores — no blur there. */
  -webkit-backdrop-filter: blur(10px) saturate(0.9);
  backdrop-filter: blur(10px) saturate(0.9);
}

.boot-card {
  width: min(480px, 100%);
  padding: 22px 24px 20px;
  border-radius: 18px;
  background: var(--surface);
  box-shadow: var(--shadow-card-hover), 0 24px 60px -24px rgba(0, 0, 0, 0.35);
  font-family: var(--font-sans);
}

/* ---- The stream ---- */
.boot-stream {
  position: relative;
  height: 136px;
  margin-bottom: 18px;
  border-radius: 12px;
  background: var(--surface-2);
  overflow: hidden;
  /* Rows fade in at the bottom and out at the top. */
  mask-image: linear-gradient(to bottom, transparent, #000 22%, #000 78%, transparent);
  -webkit-mask-image: linear-gradient(to bottom, transparent, #000 22%, #000 78%, transparent);
}
.boot-track {
  --row: 34px;
  padding: 0 14px;
  animation: boot-scroll calc(var(--rows) * 0.9s) linear infinite;
}
/* One full set of rows scrolled = seamless, because the set is rendered twice. */
@keyframes boot-scroll {
  from { transform: translateY(0); }
  to { transform: translateY(calc(var(--rows) * var(--row) * -1)); }
}
.boot-card.is-done .boot-track { animation-play-state: paused; opacity: 0.35; transition: opacity 0.3s ease; }

.boot-tx {
  height: var(--row);
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  color: var(--ink-2);
}
.boot-tx-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.boot-tx-name {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.boot-tx-amt { color: var(--ink); font-variant-numeric: tabular-nums; white-space: nowrap; }
.boot-tx-bar {
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    color-mix(in oklab, var(--ink) 10%, transparent) 0%,
    color-mix(in oklab, var(--ink) 20%, transparent) 50%,
    color-mix(in oklab, var(--ink) 10%, transparent) 100%
  );
  background-size: 200% 100%;
  animation: boot-shimmer 1.6s ease-in-out infinite;
}
.boot-tx-bar-amt { width: 52px; margin-left: auto; flex-shrink: 0; }
@keyframes boot-shimmer {
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
}

/* ---- Completion check ---- */
.boot-check {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 56px;
  height: 56px;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.boot-check-ring {
  stroke: var(--pos);
  stroke-width: 2.5;
  stroke-dasharray: 145;
  stroke-dashoffset: 145;
  animation: boot-draw 0.45s ease-out forwards;
}
.boot-check-tick {
  stroke: var(--pos);
  stroke-width: 3.5;
  stroke-dasharray: 34;
  stroke-dashoffset: 34;
  animation: boot-draw 0.3s 0.35s ease-out forwards;
}
@keyframes boot-draw { to { stroke-dashoffset: 0; } }

/* ---- Title + steps ---- */
.boot-title {
  margin: 0 0 12px;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.3;
  color: var(--ink);
}

.boot-lines {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.boot-line {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  line-height: 1.35;
  min-width: 0;
}
/* Finished lines step back so the one in progress leads. */
.boot-line.is-done { color: var(--ink-3); }
.boot-line.is-active { color: var(--ink); font-weight: 500; }
.boot-line.is-failed { color: var(--ink-2); }

.boot-mark {
  width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.is-done .boot-mark { color: var(--pos); }
.is-failed .boot-mark { color: var(--neg); }

.boot-spinner {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid color-mix(in oklab, var(--ink) 18%, transparent);
  border-top-color: var(--ink);
  animation: boot-spin 0.8s linear infinite;
}
@keyframes boot-spin { to { transform: rotate(360deg); } }

.boot-text {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}

/* ---- Segmented progress ---- */
.boot-progress { display: flex; gap: 6px; }
.boot-seg {
  flex: 1;
  height: 5px;
  border-radius: 999px;
  background: color-mix(in oklab, var(--ink) 12%, transparent);
  overflow: hidden;
  position: relative;
  transition: background 0.3s ease;
}
.boot-seg.is-done { background: var(--pos); }
.boot-seg.is-failed { background: var(--neg); }
.boot-seg.is-active::after {
  content: '';
  position: absolute;
  inset: 0;
  width: 45%;
  border-radius: inherit;
  background: color-mix(in oklab, var(--ink) 45%, transparent);
  animation: boot-sweep 1.2s ease-in-out infinite;
}
@keyframes boot-sweep {
  from { transform: translateX(-110%); }
  to { transform: translateX(240%); }
}

/* ---- Transitions ---- */
/* No enter transition on the veil: it's in the first frame the shell draws,
   already blurred, so the page is never seen sharp first. Only the card
   animates in, over the glass. On the way out both fade. */
.boot-card { animation: boot-card-in 0.35s ease-out both; }
@keyframes boot-card-in {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
}
.boot-veil-leave-active { transition: opacity 0.3s ease; }
.boot-veil-leave-active .boot-card { transition: transform 0.3s ease, opacity 0.3s ease; }
.boot-veil-leave-to { opacity: 0; }
.boot-veil-leave-to .boot-card { transform: scale(1.02); }

.boot-swap-enter-active,
.boot-swap-leave-active { transition: opacity 0.25s ease; }
.boot-swap-enter-from,
.boot-swap-leave-to { opacity: 0; }

.boot-line-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.boot-line-enter-from { opacity: 0; transform: translateY(4px); }
.boot-line-move { transition: transform 0.2s ease; }

@media (prefers-reduced-motion: reduce) {
  /* Everything still reads — a still stream, a still ring beside the checks,
     the check simply appears — just nothing moves. */
  .boot-track,
  .boot-tx-bar,
  .boot-spinner,
  .boot-seg.is-active::after { animation: none; }
  .boot-check-ring,
  .boot-check-tick { animation: none; stroke-dashoffset: 0; }
  .boot-card { animation: none; }
  .boot-veil-leave-active,
  .boot-veil-leave-active .boot-card,
  .boot-swap-enter-active,
  .boot-swap-leave-active,
  .boot-line-enter-active,
  .boot-line-move { transition: none; }
}

@media (max-width: 880px) {
  .boot-card { padding: 18px 18px 16px; }
}
</style>
